import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    InputAdornment,
    Box,
    Typography,
    Chip,
    Stack,
    Card,
    Avatar,
    Radio,
    IconButton,
    Badge,
    Skeleton
} from '@mui/material';
import {
    Search as SearchIcon,
    Security as SecurityIcon,
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { initializeDB } from '../../config/db';
import { EMPLOYEE_STORE } from '../../config/constants';

const SafetyEmployeeCard = React.memo(({ employee, isSelected, onToggle }) => {
    return (
        <Card
            sx={{
                p: 1.5,
                cursor: 'pointer',
                backgroundColor: isSelected ? '#e3f2fd' : 'white',
                border: isSelected ? '2px solid #2196f3' : '1px solid #e0e0e0',
                '&:hover': {
                    backgroundColor: isSelected ? '#e3f2fd' : '#f5f5f5'
                }
            }}
            onClick={() => onToggle(employee)}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Radio checked={isSelected} sx={{ p: 0 }} />
                <Avatar sx={{
                    backgroundColor: '#f57c00',
                    width: 36,
                    height: 36
                }}>
                    <SecurityIcon fontSize="small" />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" fontWeight="500">
                        {employee.FullName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {employee.JobClassification || 'Safety Department'}
                        {employee.Division && ` • ${employee.Division}`}
                    </Typography>
                </Box>
                <Chip
                    label="Safety"
                    size="small"
                    sx={{
                        backgroundColor: '#fff3e0',
                        color: '#f57c00',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: 20
                    }}
                />
            </Box>
        </Card>
    );
});

SafetyEmployeeCard.displayName = 'SafetyEmployeeCard';

export default function SelectSafetyEmployeeModal({ open, onClose, onSelect, selectedEmployee }) {
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        if (!open) return;
        loadSafetyEmployees();
    }, [open]);

    useEffect(() => {
        // Initialize selected from already selected (only when modal opens or selectedEmployee changes)
        if (selectedEmployee) {
            setSelected(selectedEmployee);
        }
    }, [selectedEmployee]);

    // Filter employees based on search
    const filteredEmployees = useMemo(() => {
        if (!searchTerm.trim()) {
            return employees;
        }

        const search = searchTerm.toLowerCase();
        return employees.filter(emp => {
            const name = (emp.FullName || '').toLowerCase();
            const division = (emp.Division || '').toLowerCase();
            const jobClass = (emp.JobClassification || '').toLowerCase();
            return name.includes(search) || division.includes(search) || jobClass.includes(search);
        });
    }, [searchTerm, employees]);

    async function loadSafetyEmployees() {
        setLoading(true);
        try {
            const db = await initializeDB();
            const tx = db.transaction(EMPLOYEE_STORE, 'readonly');
            const store = tx.objectStore(EMPLOYEE_STORE);
            const allEmployees = await store.getAll();

            // Map employees with normalized fields (handle different field naming conventions)
            const mappedEmployees = allEmployees.map(emp => ({
                ...emp,
                FullName: emp.FullName || emp.fullName || 'Unknown',
                Division: emp.Division || emp.division || '',
                JobClassification: emp.JobClassification || emp.jobClassification || emp.JobClass || ''
            }));

            // Filter employees with "Safety" in JobClassification or Division
            const safetyEmployees = mappedEmployees.filter(emp => {
                const jobClass = (emp.JobClassification || '').toLowerCase();
                const division = (emp.Division || '').toLowerCase();
                return jobClass.includes('safety') || division.includes('safety');
            });

            // Sort by name
            safetyEmployees.sort((a, b) => {
                const nameA = (a.FullName || '').toLowerCase();
                const nameB = (b.FullName || '').toLowerCase();
                return nameA.localeCompare(nameB);
            });

            setEmployees(safetyEmployees);
        } catch (error) {
            console.error('Failed to load safety employees:', error);
        } finally {
            setLoading(false);
        }
    }

    const isSelected = useCallback((employee) => {
        return selected?.FullName === employee.FullName;
    }, [selected]);

    const toggleSelection = useCallback((employee) => {
        setSelected(prev => {
            // Single select mode: replace selection or deselect if same
            const alreadySelected = prev && prev.FullName === employee.FullName;
            return alreadySelected ? null : employee;
        });
    }, []);

    function handleConfirm() {
        if (selected) {
            onSelect(selected);
        }
        handleClose();
    }

    function handleClose() {
        setSearchTerm('');
        onClose();
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { height: '90vh', maxHeight: 800 }
            }}
        >
            <DialogTitle sx={{ pb: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SecurityIcon color="primary" />
                        <Typography variant="h6">Select Safety Department Employee</Typography>
                    </Box>
                    <IconButton onClick={handleClose} edge="end">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: 2 }}>
                {/* Search Bar */}
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by name, division, or job classification..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ mb: 2 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />

                {/* Selected Count */}
                {selected && (
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
                        <Typography variant="body2" color="text.secondary">
                            1 selected
                        </Typography>
                        <Button
                            size="small"
                            onClick={() => setSelected(null)}
                            sx={{ ml: 'auto' }}
                        >
                            Clear
                        </Button>
                    </Box>
                )}

                {/* Employees List */}
                <Box sx={{ height: 'calc(100% - 120px)', overflow: 'auto' }}>
                    {loading ? (
                        <Stack spacing={1}>
                            {[1, 2, 3, 4, 5].map(i => (
                                <Skeleton key={i} variant="rectangular" height={72} />
                            ))}
                        </Stack>
                    ) : filteredEmployees.length === 0 ? (
                        <Card sx={{ p: 4, textAlign: 'center' }}>
                            <Typography color="text.secondary">
                                {employees.length === 0
                                    ? 'No employees with "Safety" job classification found'
                                    : 'No matching employees found'}
                            </Typography>
                        </Card>
                    ) : (
                        <Stack spacing={1}>
                            {filteredEmployees.map((emp, idx) => (
                                <SafetyEmployeeCard
                                    key={`safety-${emp.FullName}-${idx}`}
                                    employee={emp}
                                    isSelected={isSelected(emp)}
                                    onToggle={toggleSelection}
                                />
                            ))}
                        </Stack>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
                <Button onClick={handleClose}>
                    Cancel
                </Button>
                <Badge badgeContent={selected ? 1 : 0} color="primary">
                    <Button
                        variant="contained"
                        onClick={handleConfirm}
                        disabled={!selected}
                    >
                        Confirm Selection
                    </Button>
                </Badge>
            </DialogActions>
        </Dialog>
    );
}
